import { useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { cn } from '../../../lib/cn';
import type { ProductLine } from '../../customers/productLinesQueries';
import {
  PROJECT_STATUS_LABELS_HE,
  PROJECT_STATUSES,
  projectSchema,
  type ProjectCompanyOption,
  type ProjectInput,
  type ProjectRow,
} from './projects.schema';
import styles from './projects.module.css';

const DEFAULT_VALUES: ProjectInput = {
  name: '',
  status: 'quote',
  company_id: '',
  product_line_id: '',
  value_amount: 0,
  started_at: '',
  expected_end_date: '',
  notes: '',
};

function valuesFromProject(project: ProjectRow | null): ProjectInput {
  if (!project) return DEFAULT_VALUES;
  return {
    name: project.name,
    status: project.status,
    company_id: project.company_id ?? '',
    product_line_id: project.product_line_id ?? '',
    value_amount: project.value_amount,
    ...(project.hourly_rate !== null ? { hourly_rate: project.hourly_rate } : {}),
    started_at: project.started_at ?? '',
    expected_end_date: project.expected_end_date ?? '',
    notes: project.notes ?? '',
  };
}

interface ProjectFormProps {
  project: ProjectRow | null;
  companies: ProjectCompanyOption[];
  /** Pre-selects the customer on a new project (opened from a customer page). */
  defaultCompanyId?: string;
  /** Pre-selects the product line on a new project (opened from a line on the customer page). */
  defaultProductLineId?: string;
  productLines?: ProductLine[];
  formId: string;
  disabled?: boolean;
  onSubmit: (values: ProjectInput) => void | Promise<void>;
}

export function ProjectForm({
  project,
  companies,
  defaultCompanyId,
  defaultProductLineId,
  productLines = [],
  formId,
  disabled = false,
  onSubmit,
}: ProjectFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProjectInput>({
    resolver: zodResolver(projectSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const values = valuesFromProject(project);
    reset(
      project || !defaultCompanyId
        ? values
        : { ...values, company_id: defaultCompanyId, product_line_id: defaultProductLineId ?? '' },
    );
  }, [project, defaultCompanyId, defaultProductLineId, reset]);

  // A line belongs to one customer: changing the customer clears a line that no
  // longer fits. The first render is the reset above, not a user change.
  const companyId = watch('company_id') ?? '';
  const lineId = watch('product_line_id') ?? '';
  const linesForCompany = productLines.filter((l) => l.company_id === companyId);
  const seenCompany = useRef(companyId);
  useEffect(() => {
    if (seenCompany.current === companyId) return;
    seenCompany.current = companyId;
    if (lineId && !productLines.some((l) => l.id === lineId && l.company_id === companyId)) {
      setValue('product_line_id', '');
    }
  }, [companyId, lineId, productLines, setValue]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.field}>
        <label htmlFor="project-name">שם הפרויקט</label>
        <input
          id="project-name"
          type="text"
          className={cn(styles.input, errors.name && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('name')}
        />
        {errors.name ? <p className={styles.error}>{errors.name.message}</p> : null}
      </div>

      <div className={styles.row}>
        {/* Status is chosen once, at creation. After that it moves only through
            the lifecycle buttons on the project page, which reject illegal jumps. */}
        {project ? null : (
          <div className={styles.field}>
            <label htmlFor="project-status">סטטוס התחלתי</label>
            <select
              id="project-status"
              className={styles.input}
              disabled={disabled || isSubmitting}
              {...register('status')}
            >
              {PROJECT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {PROJECT_STATUS_LABELS_HE[status]}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={styles.field}>
          <label htmlFor="project-value">ערך (₪)</label>
          <input
            id="project-value"
            type="number"
            min={0}
            step="0.01"
            dir="ltr"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('value_amount', { setValueAs: (v: unknown) => (v === '' ? 0 : Number(v)) })}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="project-hourly-rate">תעריף לשעה (₪)</label>
          <input
            id="project-hourly-rate"
            type="number"
            min={0}
            step="1"
            dir="ltr"
            className={cn(styles.input, errors.hourly_rate && styles.invalid)}
            disabled={disabled || isSubmitting}
            {...register('hourly_rate', {
              setValueAs: (v: unknown) => (v === '' || v === null ? undefined : Number(v)),
            })}
          />
          {errors.hourly_rate ? <p className={styles.error}>{errors.hourly_rate.message}</p> : null}
        </div>
      </div>
      <p className={styles.hint}>
        תעריף לשעה מחשב כמה שוות השעות שנרשמו על הפרויקט. ריק = לא מחייבים לפי שעה.
      </p>

      <div className={styles.field}>
        <label htmlFor="project-company">לקוח</label>
        <select
          id="project-company"
          className={styles.input}
          disabled={disabled || isSubmitting}
          {...register('company_id')}
        >
          <option value="">ללא</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
      </div>

      {companyId ? (
        <div className={styles.field}>
          <label htmlFor="project-line">קו מוצר</label>
          <select
            id="project-line"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('product_line_id')}
          >
            <option value="">ללא קו מוצר</option>
            {linesForCompany.map((line) => (
              <option key={line.id} value={line.id}>
                {line.name}
              </option>
            ))}
          </select>
          <p className={styles.hint}>
            קו מוצר מקבץ פרויקטים של אותו לקוח, למשל "אתר" או "קמפיינים". מוסיפים קווים בדף הלקוח.
          </p>
        </div>
      ) : null}

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="project-started-at">תחילת עבודה</label>
          <input
            id="project-started-at"
            type="date"
            dir="ltr"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('started_at')}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="project-expected-end">סיום צפוי</label>
          <input
            id="project-expected-end"
            type="date"
            dir="ltr"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('expected_end_date')}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="project-notes">הערות</label>
        <textarea
          id="project-notes"
          rows={6}
          className={cn(styles.input, styles.textarea, errors.notes && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('notes')}
        />
        {errors.notes ? <p className={styles.error}>{errors.notes.message}</p> : null}
      </div>
    </form>
  );
}
